import NiceModal from "@ebay/nice-modal-react";
import { useQueryClient } from "@tanstack/react-query";
import { ArcElement, Chart as ChartJS } from "chart.js";
import clsx from "clsx";
import { endOfMonth } from "date-fns";
import { useState } from "react";
import AnimateHeight from "react-animate-height";
import { Doughnut } from "react-chartjs-2";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { ReactComponent as CaretDownIcon } from "assets/icons/caret-down.svg";
import { ReactComponent as LockIcon } from "assets/icons/lock.svg";
import Board from "components/Board";
import Box from "components/Box";
import Chip from "components/Chip";
import NoticeDetailModal from "components/modals/NoticeDetailModal";
import TitleHeading from "components/TitleHeading";
import { useGetMain } from "hooks/apis/common";
import { useGetNotices } from "hooks/apis/notice";
import { useGetBonusQuestions } from "hooks/apis/question";
import { useGetMonitoringUsersCount } from "hooks/apis/user";
import format from "lib/format";
import { userState } from "store/store";
import { Question } from "types/types";
import { questionAnswerTypeOptions } from "utils/const";

ChartJS.register(ArcElement);

const MainPage = () => {
  const user = useRecoilValue(userState);

  return (
    <main className="py-[1.875rem] px-[3.125rem]">
      <TitleHeading
        title="대시보드"
        description="웹 사이트의 전체적인 상황을 추적하는 화면입니다."
        className="mb-[0.438rem]"
      />
      <div className="flex flex-col gap-[0.625rem]">
        {user?.grade.id === 2 && <Notice />}
        {[1, 2].includes(user!.grade.id) ? <MainM /> : <MainO />}
      </div>
    </main>
  );
};

const Notice = () => {
  const { data } = useGetNotices({
    type: "notice",
    page: 1,
    size: 20,
    sort: "createdDesc",
  });
  const queryClient = useQueryClient();

  const openNoticeDetail = () => {
    if (!data?.length) {
      return;
    }
    setTimeout(() => {
      NiceModal.show(NoticeDetailModal, { noticeId: data[0].id });
      if (!data[0].isViewed) {
        queryClient.invalidateQueries({ queryKey: ["notices"] });
      }
    }, 100);
  };

  return (
    <Box
      role="button"
      onClick={openNoticeDetail}
      className="flex justify-between"
    >
      <div className="flex gap-5">
        <h3 className="text-18 font-medium">공지사항</h3>
        {data?.length && (
          <p className="text-16 font-medium text-primary">{data[0].title}</p>
        )}
      </div>
      {data?.length && (
        <span className="text-secondary text-16">
          {format(data[0].createdAt, "yyyy-MM-dd")}
        </span>
      )}
    </Box>
  );
};

const MainO = () => {
  const { data } = useGetMain();

  return (
    <div className="grid overflow-hidden grid-rows-1 grid-cols-2 gap-[0.625rem]">
      <div className="grid gap-[0.625rem]">
        <Notice />
        <Board
          title="월간 질문 현황"
          rightLabel={
            data
              ? `기간 : ${format(data.startOfMonth, "yyyy-MM-dd")} ~ ${format(endOfMonth(data.startOfMonth), "yyyy-MM-dd")}`
              : ""
          }
          values={[
            {
              label: "총 질문 수",
              value: data?.totalQuestions?.toLocaleString?.(),
              unit: "건",
              className: "text-black",
            },
            {
              label: "대기 질문 수",
              value: data?.totalQuestionsOnGoing?.toLocaleString?.(),
              unit: "건",
              className: "text-primary",
            },
            {
              label: "완료 질문 수",
              value: data
                ? (
                    data.totalQuestions - data.totalQuestionsOnGoing
                  ).toLocaleString()
                : undefined,
              unit: "건",
            },
          ]}
        />
        <Board
          title="월간 답변 현황"
          leftLabel="검수 이후 변동 될 수 있습니다."
          rightLabel={
            data
              ? `기간 : ${format(data.startOfMonth, "yyyy-MM-dd")} ~ ${format(endOfMonth(data.startOfMonth), "yyyy-MM-dd")}`
              : ""
          }
          values={[
            {
              label: "총 답변 수",
              value: data
                ? (
                    data.monthCompleted +
                    data.monthReport +
                    data.monthOngoing
                  )?.toLocaleString?.()
                : undefined,
              unit: "건",
              className: "text-black",
            },
            {
              label: "완료 답변 수",
              value: data
                ? (data.monthCompleted + data.monthOngoing)?.toLocaleString?.()
                : undefined,
              unit: "건",
              className: "text-primary",
            },
            {
              label: "경고 답변 수",
              value: data?.monthReport?.toLocaleString?.(),
              unit: "건",
              className: "text-red",
            },
          ]}
        />
        <Board
          title="일일 답변 현황"
          leftLabel="검수 이후 변동 될 수 있습니다."
          rightLabel={
            data ? `기간 : ${format(data.startOfDay, "yyyy-MM-dd")}` : ""
          }
          values={[
            {
              label: "총 답변 수",
              value: data
                ? (
                    data.dayCompleted +
                    data.dayReport +
                    data.dayOngoing
                  )?.toLocaleString?.()
                : undefined,
              unit: "건",
              className: "text-black",
            },
            {
              label: "완료 답변 수",
              value: data
                ? (data.dayCompleted + data.dayOngoing)?.toLocaleString?.()
                : undefined,
              unit: "건",
              className: "text-primary",
            },
            {
              label: "경고 답변 수",
              value: data?.dayReport?.toLocaleString?.(),
              unit: "건",
              className: "text-red",
            },
          ]}
        />
      </div>
      <div />
    </div>
  );
};

const BonusQuestion = () => {
  const { data: bonusQuestions } = useGetBonusQuestions();
  const navigate = useNavigate();

  const goToQuestion = ({ question }: { question: Question }) => {
    if (question.status === "작성중") {
      return;
    }
    navigate({
      pathname: `/qna/${question.bNo}`,
      search: `?${createSearchParams(
        JSON.parse(
          JSON.stringify({
            bonus: true,
          }),
        ),
      )}`,
    });
  };

  return (
    <Board
      className="flex flex-col overflow-hidden"
      title="Bonus Questions"
      leftLabel={() => (
        <div className="text-12 relative -bottom-[0.125rem]">
          <span className="text-secondary">
            아래 질문에 답변시 보너스 금액이 지급됩니다.
          </span>
          <span className="text-red">
            {" "}
            (단, 이관/경고 처리된 경우 추가 지급되지 않습니다.)
          </span>
        </div>
      )}
      rightLabel="업데이트 주기 : 매일 자정 10개 선정"
    >
      <table className="flex-1 flex flex-col overflow-hidden w-full space-y-[0.625rem] pb-[0.875rem]">
        <thead className="text-white text-16 block w-full">
          <tr className="flex h-[3rem] items-center w-full rounded-md bg-secondary">
            <th className="max-w-[6.25rem] min-w-[5rem] w-[12%]">번호</th>
            <th className="max-w-[6.25rem] min-w-[5rem] w-[12%]">상태</th>
            <th className="max-w-[6.25rem] min-w-[5rem] w-[12%]">과목유형</th>
            <th className="flex-1 px-5 text-left">제목</th>
            <th className="max-w-[6.25rem] min-w-[5rem] w-[12%]">보너스</th>
          </tr>
        </thead>
        <tbody className="flex flex-col flex-1 overflow-auto space-y-[0.625rem] text-secondary text-16 font-medium text-center">
          {bonusQuestions?.map((question, index) => (
            <tr
              key={question.bNo}
              onClick={() => goToQuestion({ question })}
              className="flex h-[3rem] shrink-0 items-center rounded-md border border-gray"
            >
              <td className="max-w-[6.25rem] min-w-[5rem] w-[12%]">
                {index + 1}
              </td>
              <td className="max-w-[6.25rem] min-w-[5rem] w-[12%]">
                <Chip
                  color={
                    question.status === "대기중"
                      ? "red"
                      : question.status === "답변 완료"
                        ? "blue"
                        : question.status === "작성중"
                          ? "gray"
                          : "orange"
                  }
                >
                  {question.status === "답변 완료" ? "완료" : question.status}
                </Chip>
              </td>
              <td className="max-w-[6.5rem] min-w-[5rem] w-[25%]">
                {question.subjectType}
              </td>
              <td className="flex-1 px-5">
                <div className="flex items-center">
                  {question.secret && <LockIcon className="size-6 shrink-0" />}
                  <p className="text-left line-clamp-1 break-all">
                    {question.title}
                  </p>
                </div>
              </td>
              <td className="max-w-[6.25rem] min-w-[5rem] w-[12%]">
                +{question.bonus}원
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Board>
  );
};

export const COLORS: Record<
  string,
  { color: string; backgroundColor: string }
> = {
  "독서(교재)": { color: "#7776B3", backgroundColor: "bg-[#7776B3]" },
  "문학(교재)": { color: "#9B86BD", backgroundColor: "bg-[#9B86BD]" },
  "화작,매체(교재)": { color: "#3D487A", backgroundColor: "bg-[#3D487A]" }, // More distinct
  "문법(교재)": { color: "#5A639C", backgroundColor: "bg-[#5A639C]" },
  "독서(강의)": { color: "#B45CE0", backgroundColor: "bg-[#B45CE0]" }, // More vivid
  "문학(강의)": { color: "#672E97", backgroundColor: "bg-[#672E97]" }, // Deep contrast
  "화작,매체(강의)": { color: "#2F275D", backgroundColor: "bg-[#2F275D]" }, // Dark and distinct
  "문법(강의)": { color: "#C7A7D8", backgroundColor: "bg-[#C7A7D8]" },
  "기타(행정, 학습법 등)": {
    color: "#E2BBE9",
    backgroundColor: "bg-[#E2BBE9]",
  },
};

const MainM = () => {
  const { data: counts } = useGetMonitoringUsersCount();
  const { data } = useGetMain();
  const user = useRecoilValue(userState);
  const [height, setHeight] = useState<0 | "auto">("auto");

  const toggleHeight = () => {
    setHeight((h) => (h ? 0 : "auto"));
  };

  const totalQuestions = data
    ? data.easyCount + data.mediumCount + data.hardCount || 1
    : 1;

  const types = questionAnswerTypeOptions
    .slice(1, questionAnswerTypeOptions.length)
    .map((c) => ({
      label: c.label,
      color: COLORS[c.label]?.color,
      backgroundColor: COLORS[c.label]?.backgroundColor,
      value: (
        data?.classificationTypeCounts?.find?.(
          (v) => v.classificationType === c.label,
        )?.count || 0
      ).toLocaleString(),
    }))
    .sort(function (a, b) {
      return +b.value - +a.value;
    });

  return (
    <>
      <Box className="mb-[0.313rem]">
        <h3 className="text-18 font-medium">질문 및 답변 현황</h3>
      </Box>
      <div className="grid h-[44.75rem] overflow-hidden grid-rows-1 grid-cols-2 gap-[0.625rem]">
        <div className="grid grid-rows-3 gap-[0.625rem]">
          <Board
            title="월간 질문 현황"
            rightLabel={
              data
                ? `기간 : ${format(data.startOfMonth, "yyyy-MM-dd")} ~ ${format(endOfMonth(data.startOfMonth), "yyyy-MM-dd")}`
                : ""
            }
            values={[
              {
                label: "총 질문 수",
                value: data?.totalQuestions?.toLocaleString?.(),
                unit: "건",
                className: "text-black",
              },
              {
                label: "대기 질문 수",
                value: data?.totalQuestionsOnGoing?.toLocaleString?.(),
                unit: "건",
                className: "text-primary",
              },
              {
                label: "완료 질문 수",
                value: data
                  ? (
                      data.totalQuestions - data.totalQuestionsOnGoing
                    ).toLocaleString()
                  : undefined,
                unit: "건",
              },
            ]}
          />
          {user?.grade.id === 1 && (
            <Board
              title="월간 이관 질문 현황"
              rightLabel={
                data
                  ? `기간 : ${format(data.startOfMonth, "yyyy-MM-dd")} ~ ${format(endOfMonth(data.startOfMonth), "yyyy-MM-dd")}`
                  : ""
              }
              values={[
                {
                  label: "총 이관 질문 수",
                  value: data?.totalTransfers?.toLocaleString?.(),
                  unit: "건",
                  className: "text-black",
                },
                {
                  label: "대기 이관 질문 수",
                  value: data?.totalTransfersNotAnswered?.toLocaleString?.(),
                  unit: "건",
                  className: "text-primary",
                },
                {
                  label: "완료 이관 질문 수",
                  value: data
                    ? (
                        data.totalTransfers - data.totalTransfersNotAnswered
                      )?.toLocaleString?.()
                    : undefined,
                  unit: "건",
                },
              ]}
            />
          )}
          <Board
            title="월간 검수 현황"
            rightLabel={
              data
                ? `기간 : ${format(data.startOfMonth, "yyyy-MM-dd")} ~ ${format(endOfMonth(data.startOfMonth), "yyyy-MM-dd")}`
                : ""
            }
            values={[
              {
                label: "총 검수 수",
                value: data
                  ? (
                      data.monthCompleted + data.monthOngoing
                    )?.toLocaleString?.()
                  : undefined,
                unit: "건",
                className: "text-black",
              },
              {
                label: "대기 검수 수",
                value: data?.monthOngoing?.toLocaleString?.(),
                unit: "건",
                className: "text-primary",
              },
              {
                label: "완료 검수 수",
                value: data?.monthCompleted?.toLocaleString?.(),
                unit: "건",
              },
            ]}
          />
        </div>
        <Board
          className="flex flex-col"
          title="월간 질문 통계"
          rightLabel={
            data
              ? `기간 : ${format(data.startOfMonth, "yyyy-MM-dd")} ~ ${format(endOfMonth(data.startOfMonth), "yyyy-MM-dd")}`
              : ""
          }
        >
          <div className="flex flex-1 items-center">
            <div className="w-[50%] flex flex-col items-center">
              <h4 className="font-bold text-18 text-center mb-[2.375rem]">
                질문 난이도
              </h4>
              <div className="aspect-square w-[18.75rem] max-w-[50%]">
                <Doughnut
                  data={{
                    datasets: [
                      {
                        borderWidth: 0,
                        backgroundColor: [
                          "#3AA6B9",
                          "#F9F9E0",
                          "#FFD0D0",
                          "#F6CA56",
                        ],
                        data: [
                          data?.easyCount,
                          data?.mediumCount,
                          data?.hardCount,
                          data?.cantAnswerCount,
                        ],
                      },
                    ],
                  }}
                  options={{
                    rotation: -90,
                    events: [],
                    maintainAspectRatio: false,
                  }}
                />
              </div>
              <ul className="max-w-[12.625rem] h-[8.5rem] w-full mt-[2.5rem] mx-auto space-y-[0.313rem]">
                {[
                  {
                    color: "bg-[#3AA6B9]",
                    label: "쉬움(5분 이내 처리 가능)",
                    value: `${data ? data.easyCount.toLocaleString() : 0} (${Math.round(((data?.easyCount ?? 0) / totalQuestions) * 100)}%)`,
                  },
                  {
                    color: "bg-[#F9F9E0]",
                    label: "중간(10분 이내 처리 가능)",
                    value: `${data ? data.mediumCount.toLocaleString() : 0} (${Math.round(((data?.mediumCount ?? 0) / totalQuestions) * 100)}%)`,
                  },
                  {
                    color: "bg-[#FFD0D0]",
                    label: "어려움(10분 초과 소요)",
                    value: `${data ? data.hardCount.toLocaleString() : 0} (${Math.round(((data?.hardCount ?? 0) / totalQuestions) * 100)}%)`,
                  },
                  {
                    color: "bg-[#F6CA56]",
                    label: "답변 불가",
                    value: `${data ? data.cantAnswerCount.toLocaleString() : 0} (${Math.round(((data?.cantAnswerCount ?? 0) / totalQuestions) * 100)}%)`,
                  },
                ].map(({ color, label, value }) => (
                  <li key={label} className="flex justify-between">
                    <div className="flex items-center gap-[0.625rem]">
                      <span
                        className={clsx("size-[0.625rem] rounded-full", color)}
                      ></span>
                      <span className="text-12 text-secondary">{label}</span>
                    </div>
                    <span className="text-12 text-secondary">{value}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-[50%] flex flex-col items-center">
              <h4 className="font-bold text-18 text-center mb-[2.375rem]">
                질문 유형
              </h4>
              <div className="aspect-square w-[18.75rem] max-w-[50%]">
                <Doughnut
                  data={{
                    datasets: [
                      {
                        borderWidth: 0,
                        backgroundColor: types.map(({ color }) => color),
                        data: types.map(({ value }) => value),
                      },
                    ],
                  }}
                  options={{
                    rotation: -90,
                    events: [],
                    maintainAspectRatio: false,
                  }}
                />
              </div>
              <ul className="max-w-[12.625rem] min-w-fit h-[8.5rem] flex flex-col 1400:flex-wrap w-full mt-[2.5rem] mx-auto gap-y-[0.313rem] gap-x-3 1500:gap-x-5">
                {types
                  .map((type) => ({
                    color: type.backgroundColor,
                    label: type.label,
                    value: `${type.value.toLocaleString()} (${Math.round((+type.value / totalQuestions) * 100)}%)`,
                  }))
                  .map(({ color, label, value }) => (
                    <li
                      key={label}
                      className="flex justify-between space-x-1 1500:space-x-0 min-w-28 1500:min-w-44"
                    >
                      <div className="flex items-center gap-[0.625rem]">
                        <span
                          className={clsx(
                            "size-[0.625rem] rounded-full",
                            color,
                          )}
                        ></span>
                        <span className="text-12 text-secondary">{label}</span>
                      </div>
                      <span className="text-12 text-secondary">{value}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </Board>
      </div>
      <Box
        role="button"
        onClick={toggleHeight}
        className="flex justify-between items-center mt-[0.625rem] mb-[0.313rem]"
      >
        <h3 className="text-18 font-medium">조교 현황</h3>
        <CaretDownIcon
          className={clsx("size-[0.875rem] duration-300", {
            "-rotate-180": height === 0,
          })}
        />
      </Box>
      <AnimateHeight height={height} duration={300}>
        <div className="flex gap-[0.625rem]">
          <div className="flex-[3]">
            <Board
              title="조교 접속 현황"
              rightLabel={`조회 시간 : ${format(counts?.date, "yyyy-MM-dd HH:mm:ss")}`}
              values={[
                {
                  label: "총 조교 수",
                  value: `${counts?.totalUsers ?? 0}`,
                  unit: "명",
                  className: "text-black",
                },
                {
                  label: "접속중인 조교",
                  value: `${counts?.connectedUsers ?? 0}`,
                  unit: "명",
                  className: "text-primary",
                },
                {
                  label: "미접속 조교",
                  value: `${counts ? counts.totalUsers - counts.connectedUsers : 0}`,
                  unit: "명",
                },
              ]}
            />
          </div>
          <div className="flex-[2]">
            <Board
              title="아이디 연동 현황"
              rightLabel={`조회 시간 : ${format(counts?.date, "yyyy-MM-dd HH:mm:ss")}`}
              values={[
                {
                  label: "정상 연동",
                  value: `${counts ? counts.totalUsers - counts.unlinkedUsers : 0}`,
                  unit: "건",
                  className: "text-black",
                },
                {
                  label: "비정상 연동",
                  value: `${counts?.unlinkedUsers ?? 0}`,
                  unit: "건",
                  className: "text-red",
                },
              ]}
            />
          </div>
        </div>
      </AnimateHeight>
    </>
  );
};

export default MainPage;
